import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ConfigProvider, Dialog } from 'react-vant' // 确保正确导入
import { fetchUserInfo } from './store/userSlice'
import Login from './pages/login'
import TeacherHome from './pages/teacher/Home'
import SportsHome from './pages/teacher/SportsHome'
import StudentHome from './pages/student/StudentHome'
import StudentSportsHome from './pages/student/StudentSportsHome'
import PhysicalTestIndex from './pages/physical-test'
import Record from './pages/physical-test/record'
import BulkRecord from './pages/physical-test/BulkRecord'
import Query from './pages/physical-test/query'
import Profile from './pages/profile'
import TabBarLayout from './components/TabBarLayout'
import AdminDashboard from './pages/admin/Dashboard'
import AdminDataInsight from './pages/admin/DataInsight'
import AdminNotificationRelease from './pages/admin/NotificationRelease'
import TeacherClasses from './pages/teacher/Classes'
import TeacherStudents from './pages/teacher/Students'
import PhysicalTestCalculator from './pages/physical-test/calculator'
import PhysicalTestAnalytics from './pages/physical-test/Analytics'
import PhysicalTestExport from './pages/physical-test/Export'
import RefereeLogin from './pages/sports/RefereeLogin'
import ClubList from './pages/clubs/ClubList'
import ClubRegister from './pages/clubs/ClubRegister'
import MyRegistrations from './pages/clubs/MyRegistrations'
import MyClubs from './pages/clubs/MyClubs'
import ClubSpace from './pages/clubs/ClubSpace'
import ClubWorkspace from './pages/clubs/ClubWorkspace'
import ClubAttendance from './pages/teacher/clubs/ClubAttendance'
import ClubAttendanceHistory from './pages/teacher/clubs/ClubAttendanceHistory'
import ClubLeaveReview from './pages/teacher/clubs/ClubLeaveReview'
import ClubBroadcast from './pages/teacher/clubs/ClubBroadcast'
import ClubAlbum from './pages/teacher/clubs/ClubAlbum'
import ClubEvaluation from './pages/teacher/clubs/ClubEvaluation'
import AdminClubs from './pages/admin/clubs/AdminClubs'
import AdminClubEdit from './pages/admin/clubs/AdminClubEdit'
import AdminSessions from './pages/admin/clubs/AdminSessions'
import AdminSessionEdit from './pages/admin/clubs/AdminSessionEdit'
import RefereeWorkbench from './pages/sports/RefereeWorkbench'
import RefereeSuccess from './pages/sports/RefereeSuccess'
import ClassMatch from './pages/sports/ClassMatch'
import ClassMatchLeague from './pages/sports/ClassMatchLeague'
import ClassMatchScorer from './pages/sports/ClassMatchScorer'
import SportsRegistration from './pages/sports/Registration'
import SportsLive from './pages/sports/Live'
import LiveDetail from './pages/sports/LiveDetail'
import SportsSchedule from './pages/sports/Schedule'
import SportsRank from './pages/sports/Rankings'
import SportsReports from './pages/sports/Reports'
import SportsAthletes from './pages/sports/Athletes'
import AthleteProfile from './pages/sports/AthleteProfile'
import StudentDetail from './pages/teacher/StudentDetail'
import MyTimetable from './pages/teacher/MyTimetable'
import AffairsDashboard from './pages/teacher/affairs/AffairsDashboard'
import AdminPendingLeave from './pages/teacher/affairs/AdminPendingLeave'
import AdminPendingSubstitute from './pages/teacher/affairs/AdminPendingSubstitute'
import AdminOverview from './pages/teacher/affairs/AdminOverview'
import DutyPage from './pages/teacher/affairs/DutyPage'
import OvertimePage from './pages/teacher/affairs/OvertimePage'
import LeavePage from './pages/teacher/affairs/LeavePage'
import SubstitutePage from './pages/teacher/affairs/SubstitutePage'
import PhysicalTestDetail from './pages/physical-test/Detail'
import NationalStandards from './pages/physical-test/NationalStandards'
import FitnessPortfolio from './pages/physical-test/Portfolio'
import ExemptionApply from './pages/physical-test/Exemption'
import NotificationList from './pages/notifications'
import Feedback from './pages/profile/Feedback'
import HubIndex from './pages/hub/HubIndex'
import PostDetail from './pages/hub/PostDetail'
import CreatePost from './pages/hub/CreatePost'

// 路由守卫
function PrivateRoute({ children, allowedRoles }) {
  const { isAuthenticated, userInfo } = useSelector(state => state.user)
  // 支持 URL 参数传参 token (如从 App 嵌套加载或单点跳转)
  const searchParams = new URLSearchParams(window.location.search)
  const queryToken = searchParams.get('token')
  if (queryToken && !localStorage.getItem('token')) {
    localStorage.setItem('token', queryToken)
  }
  const token = localStorage.getItem('token') || queryToken

  if (!isAuthenticated && !token) {
    return <Navigate to="/login" replace />
  }

  if (allowedRoles && userInfo && !allowedRoles.includes(userInfo?.role)) {
    // 权限不足时的重定向逻辑
    const defaultPath = userInfo.role === 'admin' ? '/admin' :
      (userInfo.role === 'teacher' ? '/' : '/student')
    return <Navigate to={defaultPath} replace />
  }

  return children
}

// 主题配置 - 对齐主页「星翼墨绿」设计体系（唯一真源：src/theme/brand.css）
// 注意：react-vant 的 themeVars 只会写到 ConfigProvider 自己的 wrapper 行内样式上，
// 而 Dialog.alert / Popup 通过 Portal 挂到 document.body（wrapper 的兄弟节点），
// 那些弹窗继承不到行内变量 → 仍是默认蓝。
// 因此真正的品牌色统一放在 src/theme/brand.css 的 :root 上（全局生效），
// 这里只保留少量「与 react-vant 默认值不同、需要单独覆盖」的项。
const themeVars = {
  // 弹窗类组件走 Portal，需要在行内样式上再兜一次（react-vant 的 Tag/Button 等
  // 已由 :root 统一覆盖，此处仅覆盖弹窗内部读得到的变量）
  dialogConfirmButtonTextColor: '#056b4f',
  dialogHasTitleMessageTextColor: '#475569',
}

function App() {
  const dispatch = useDispatch()
  const { isAuthenticated, userInfo } = useSelector(state => state.user)

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const queryToken = searchParams.get('token')
    if (queryToken) {
      localStorage.setItem('token', queryToken)
    }
    const token = localStorage.getItem('token') || queryToken
    if (token && !isAuthenticated) {
      dispatch(fetchUserInfo())
    }
  }, [dispatch, isAuthenticated])

  // 维护模式弹窗标识，使用 useRef 确保在组件重渲染时保持状态
  const maintenanceDialogOpen = useRef(false);

  // 监听系统维护全局事件
  useEffect(() => {
    const handleMaintenance = (e) => {
      // 如果当前是普通用户且弹窗未打开，弹出警告
      if (userInfo?.role !== 'admin' && !maintenanceDialogOpen.current) {
        maintenanceDialogOpen.current = true;
        Dialog.alert({
          title: '系统维护中',
          message: e.detail.message || '系统正在进行维护，请稍后再试。',
          confirmButtonText: '知道了',
          closeOnClickOverlay: false
        }).then(() => {
          maintenanceDialogOpen.current = false;
        });
      }
    };

    window.addEventListener('SYSTEM_MAINTENANCE', handleMaintenance);
    return () => window.removeEventListener('SYSTEM_MAINTENANCE', handleMaintenance);
  }, [userInfo?.role]);

  return (
    <ConfigProvider themeVars={themeVars}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          
          {/* 裁判端公开路由 - 无需教师登录 */}
          <Route path="/sports/referee">
            <Route path="login" element={<RefereeLogin />} />
            <Route path="workbench" element={<RefereeWorkbench />} />
            <Route path="success" element={<RefereeSuccess />} />
          </Route>
          
          {/* 公开路由 - 赛事直播 */}
          <Route path="/sports/live" element={<SportsLive />} />
          <Route path="/sports/live/:id" element={<LiveDetail />} />

          {/* 管理员路由 */}
          <Route path="/admin/*" element={
            <PrivateRoute allowedRoles={['admin']}>
              <Routes>
                <Route index element={<AdminDashboard />} />
                <Route path="data-insight" element={<AdminDataInsight />} />
                <Route path="notification-release" element={<AdminNotificationRelease />} />
              </Routes>
            </PrivateRoute>
          } />

          {/* 教师端路由 */}
          <Route path="/" element={
            <PrivateRoute allowedRoles={['teacher', 'admin']}>
              <TabBarLayout />
            </PrivateRoute>
          }>
            <Route index element={<TeacherHome />} />

            <Route path="physical-test">
              <Route index element={<PhysicalTestIndex />} />
              <Route path="record" element={<Record />} />
              <Route path="bulk-record" element={<BulkRecord />} />
              <Route path="query" element={<Query />} />
              <Route path="detail/:testId" element={<PhysicalTestDetail />} />
              <Route path="calculator" element={<PhysicalTestCalculator />} />
              <Route path="standards" element={<NationalStandards />} />
              <Route path="analytics" element={<PhysicalTestAnalytics />} />
              <Route path="export" element={<PhysicalTestExport />} />
              <Route path="exemption" element={<ExemptionApply />} />
              <Route path="portfolio" element={<FitnessPortfolio />} />
            </Route>

            <Route path="teacher">
              <Route path="classes" element={<TeacherClasses />} />
              <Route path="students" element={<TeacherStudents />} />
              <Route path="student-detail/:studentId" element={<StudentDetail />} />
              <Route path="my-timetable" element={<MyTimetable />} />
              <Route path="affairs" element={<AffairsDashboard />} />
              <Route path="affairs/duty" element={<DutyPage />} />
              <Route path="affairs/overtime" element={<OvertimePage />} />
              <Route path="affairs/leave" element={<LeavePage />} />
              <Route path="affairs/substitute" element={<SubstitutePage />} />
              {/* 管理端（admin/staff）：全局管理视角 */}
              <Route path="affairs/admin/pending-leave" element={<AdminPendingLeave />} />
              <Route path="affairs/admin/pending-substitute" element={<AdminPendingSubstitute />} />
              <Route path="affairs/admin/overview" element={<AdminOverview />} />
            </Route>

            <Route path="sports">
              <Route index element={<SportsHome />} />
              <Route path="class-match" element={<ClassMatch />} />
              <Route path="class-match/:matchId" element={<ClassMatchLeague />} />
              <Route path="class-match/:matchId/score/:scheduleId" element={<Navigate to="/student/sports/class-match" replace />} />
              <Route path="registration" element={<SportsRegistration />} />
              <Route path="schedule" element={<SportsSchedule />} />
              <Route path="athletes" element={<SportsAthletes />} />
              <Route path="athlete/:id" element={<AthleteProfile />} />
              <Route path="reports" element={<SportsReports />} />
              <Route path="rank" element={<SportsRank />} />
            </Route>

            <Route path="notifications" element={<NotificationList />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="profile" element={<Profile />} />
            
            <Route path="hub">
              <Route index element={<HubIndex />} />
              <Route path="post/:id" element={<PostDetail />} />
              <Route path="create" element={<CreatePost />} />
            </Route>
            
            <Route path="clubs">
              <Route index element={<ClubList />} />
              <Route path="register/:sessionId" element={<ClubRegister />} />
              <Route path="my-registrations" element={<MyRegistrations />} />
              <Route path="teacher">
                <Route index element={<ClubWorkspace />} />
                <Route path="attendance/:sessionId" element={<ClubAttendance />} />
                <Route path="attendance/history/:sessionId" element={<ClubAttendanceHistory />} />
                <Route path="leave-review/:sessionId" element={<ClubLeaveReview />} />
                <Route path="leave-review" element={<ClubLeaveReview />} />
                <Route path="broadcast/:sessionId" element={<ClubBroadcast />} />
                <Route path="broadcast" element={<ClubBroadcast />} />
                <Route path="album/:sessionId" element={<ClubAlbum />} />
                <Route path="evaluation/:sessionId" element={<ClubEvaluation />} />
              </Route>

              {/* 管理员专属路由 - 独立于教师路径 */}
              <Route path="admin">
                <Route index element={<ClubWorkspace />} />
                <Route path="clubs" element={<PrivateRoute allowedRoles={['admin']}><AdminClubs /></PrivateRoute>} />
                <Route path="club-edit/:clubId" element={<PrivateRoute allowedRoles={['admin']}><AdminClubEdit /></PrivateRoute>} />
                <Route path="sessions" element={<ClubWorkspace />} />
                <Route path="session-edit/:sessionId" element={<PrivateRoute allowedRoles={['admin']}><AdminSessionEdit /></PrivateRoute>} />
              </Route>
            </Route>
          </Route>

          {/* 学生端路由 */}
          <Route path="/student" element={
            <PrivateRoute allowedRoles={['parent', 'student']}>
              <TabBarLayout type="student" />
            </PrivateRoute>
          }>
            <Route index element={<StudentHome />} />

            <Route path="physical-test">
              <Route path="query" element={<Query />} />
              <Route path="detail/:testId" element={<PhysicalTestDetail />} />
              <Route path="calculator" element={<PhysicalTestCalculator />} />
              <Route path="standards" element={<NationalStandards />} />
              <Route path="portfolio" element={<FitnessPortfolio />} />
              <Route path="exemption" element={<ExemptionApply />} />
            </Route>

            <Route path="clubs">
              <Route index element={<ClubList />} />
              <Route path="register/:sessionId" element={<ClubRegister />} />
              <Route path="my-registrations" element={<MyRegistrations />} />
              <Route path="my" element={<MyClubs />} />
              <Route path="space/:sessionId/:studentId" element={<ClubSpace />} />
            </Route>

            <Route path="hub">
              <Route index element={<HubIndex />} />
              <Route path="post/:id" element={<PostDetail />} />
              <Route path="create" element={<CreatePost />} />
            </Route>

            <Route path="sports">
              <Route index element={<StudentSportsHome />} />
              <Route path="class-match" element={<ClassMatch />} />
              <Route path="class-match/:matchId" element={<ClassMatchLeague />} />
              <Route path="class-match/:matchId/score/:scheduleId" element={<ClassMatchScorer />} />
              <Route path="schedule" element={<SportsSchedule />} />
              <Route path="rank" element={<SportsRank />} />
              <Route path="athletes" element={<SportsAthletes />} />
              <Route path="athlete/:id" element={<AthleteProfile />} />
              <Route path="live" element={<SportsLive />} />
              <Route path="live/:id" element={<LiveDetail />} />
            </Route>

            <Route path="notifications" element={<NotificationList />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          {/* 遗产路由重定向：兼容 App 端硬编码的旧链接 */}
          <Route path="/parent/physical-test/list" element={<Navigate to="/student/physical-test/query" replace />} />
          <Route path="/parent/*" element={<Navigate to={window.location.pathname.replace('/parent', '/student')} replace />} />
          
          {/* 顶级路径兼容：针对 App 直接跳转 https://m.d2ty.cn/physical-test/query 等情况 */}
          <Route path="/physical-test/*" element={<Navigate to={window.location.pathname.replace('/physical-test', '/student/physical-test')} replace />} />
          <Route path="/clubs/*" element={<Navigate to={window.location.pathname.replace('/clubs', '/student/clubs')} replace />} />
          <Route path="/sports/*" element={<Navigate to={window.location.pathname.replace('/sports', '/student/sports')} replace />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  )
}

export default App
